import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Share, Plus, X, Smartphone } from 'lucide-react';

const DISMISS_KEY = 'pwa-install-dismissed';
const DISMISS_DAYS = 14;
const IDLE_DELAY_MS = 15000;

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

const dismissedRecently = () => {
  const at = localStorage.getItem(DISMISS_KEY);
  if (!at) return false;
  return (Date.now() - Number(at)) / 86400000 < DISMISS_DAYS;
};

const InstallPWA = () => {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [visible, setVisible] = useState(false);
  const [showIOSSteps, setShowIOSSteps] = useState(false);
  const installable = useRef(false);
  const armed = useRef(false);

  const reveal = useCallback(() => {
    if (!installable.current || isStandalone() || dismissedRecently()) return;
    setVisible(true);
  }, []);

  useEffect(() => {
    if (isStandalone()) return undefined;

    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const iOSSafari = iOS && !/CriOS|FxiOS|EdgiOS/.test(navigator.userAgent);
    setIsIOS(iOS);
    if (iOSSafari) installable.current = true;

    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      installable.current = true;
      if (armed.current) reveal();
    };
    const onInstalled = () => {
      setVisible(false);
      localStorage.setItem(DISMISS_KEY, Date.now().toString());
    };
    // Fired from high-intent moments (e.g. right after a successful booking)
    const onRequest = () => {
      armed.current = true;
      reveal();
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onInstalled);
    window.addEventListener('enayahub:install-prompt', onRequest);

    const idle = setTimeout(() => {
      armed.current = true;
      reveal();
    }, IDLE_DELAY_MS);

    return () => {
      clearTimeout(idle);
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onInstalled);
      window.removeEventListener('enayahub:install-prompt', onRequest);
    };
  }, [reveal]);

  const handleInstall = async () => {
    if (isIOS || !deferredPrompt) {
      setShowIOSSteps(true);
      return;
    }
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    if (outcome === 'accepted') setVisible(false);
  };

  const handleDismiss = () => {
    setVisible(false);
    setShowIOSSteps(false);
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
  };

  if (!visible) return null;

  return (
    <>
      <div
        data-testid="pwa-install-banner"
        className="fixed inset-x-3 bottom-[calc(0.75rem+env(safe-area-inset-bottom))] z-[55] md:inset-x-auto md:end-6 md:w-[26rem]"
      >
        <div className="relative overflow-hidden rounded-3xl border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-900 shadow-2xl shadow-blue-950/20">
          <span className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-600 via-teal-500 to-emerald-500" />
          <div className="flex items-start gap-4 p-5">
            <span className="grid place-items-center w-12 h-12 shrink-0 rounded-2xl bg-gradient-to-br from-blue-600 to-emerald-500 shadow-lg shadow-blue-600/25">
              <Smartphone className="w-6 h-6 text-white" />
            </span>
            <div className="min-w-0 flex-1">
              <h3 className="font-display text-base font-bold text-gray-900 dark:text-white">
                {t('pwa.title', 'Add EnayaHub to your home screen')}
              </h3>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                {t('pwa.subtitle', 'Book in one tap, get reminders, and open it like an app — no store needed.')}
              </p>
              <div className="mt-4 flex items-center gap-2">
                <button
                  onClick={handleInstall}
                  data-testid="pwa-install-btn"
                  className="btn-shine inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-gradient-to-r from-blue-600 to-emerald-500 px-4 py-2.5 text-sm font-bold text-white shadow-lg shadow-blue-600/25"
                >
                  <Download className="w-4 h-4" />
                  {isIOS ? t('pwa.howTo', 'Show me how') : t('pwa.install', 'Install app')}
                </button>
                <button
                  onClick={handleDismiss}
                  data-testid="pwa-dismiss-btn"
                  className="whitespace-nowrap rounded-full px-3 py-2.5 text-sm font-semibold text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
                >
                  {t('pwa.later', 'Not now')}
                </button>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              aria-label={t('common.close', 'Close')}
              data-testid="pwa-close-btn"
              className="grid place-items-center w-8 h-8 shrink-0 rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {showIOSSteps && (
        <div className="fixed inset-0 z-[60] grid place-items-end sm:place-items-center p-3" data-testid="pwa-ios-sheet">
          <button
            aria-label={t('common.close', 'Close')}
            onClick={() => setShowIOSSteps(false)}
            className="absolute inset-0 bg-gray-950/60 cursor-default"
          />
          <div className="relative w-full sm:max-w-md rounded-3xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/10 p-6 shadow-2xl">
            <h3 className="font-display text-lg font-bold text-gray-900 dark:text-white">
              {t('pwa.iosTitle', 'Add to Home Screen')}
            </h3>
            <ol className="mt-5 space-y-4">
              {[
                {
                  icon: Share,
                  text: t('pwa.iosStep1', 'Tap the Share button in the Safari toolbar'),
                },
                {
                  icon: Plus,
                  text: t('pwa.iosStep2', 'Scroll down and choose "Add to Home Screen"'),
                },
                {
                  icon: Smartphone,
                  text: t('pwa.iosStep3', 'Tap Add — EnayaHub now opens like an app'),
                },
              ].map((step, i) => {
                const Icon = step.icon;
                return (
                  <li key={i} className="flex items-start gap-3">
                    <span className="grid place-items-center w-9 h-9 shrink-0 rounded-xl bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400">
                      <Icon className="w-4 h-4" />
                    </span>
                    <span className="pt-1.5 text-sm text-gray-700 dark:text-gray-200">{step.text}</span>
                  </li>
                );
              })}
            </ol>
            <button
              onClick={handleDismiss}
              data-testid="pwa-ios-done-btn"
              className="mt-6 w-full rounded-full bg-gray-900 dark:bg-white px-5 py-3 text-sm font-bold text-white dark:text-gray-900"
            >
              {t('pwa.gotIt', 'Got it')}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallPWA;
